import type { CheckoutResult, TopUpOrder } from '@wa/shared';

/**
 * Razorpay Checkout in the portal.
 *
 * The script is Razorpay's own and is loaded once, on first use — not in
 * index.html, because most sessions never open Billing and the agent on the inbox
 * should not download a payment SDK. Everything the modal needs comes from the
 * order the API created; nothing here decides an amount.
 *
 * What comes back is three ids. They are posted to the API, which verifies the
 * signature under the key secret and asks Razorpay whether the money was captured
 * before a rupee moves — the browser is a messenger here, not a witness.
 */

const SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler: (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => void;
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (event: { error?: { description?: string; reason?: string } }) => void) => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

let loading: Promise<void> | null = null;

export function loadCheckout(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('The payment window could not be loaded. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface CheckoutHandlers {
  onPaid: (result: CheckoutResult) => void;
  onDismiss: () => void;
  onFailed: (reason: string | null) => void;
}

export async function openCheckout(
  order: TopUpOrder,
  prefill: { name?: string; email?: string },
  handlers: CheckoutHandlers,
): Promise<void> {
  await loadCheckout();
  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('The payment window could not be loaded.');

  const instance = new Razorpay({
    key: order.keyId,
    amount: Math.round(order.gross * 100),
    currency: order.currency,
    name: 'PSM Softtech',
    description: `Wallet top-up · ₹${order.taxable.toLocaleString('en-IN')} credit + GST`,
    order_id: order.orderId,
    prefill,
    notes: { payment_id: String(order.paymentId) },
    theme: { color: '#15803d' },
    handler: (r) => handlers.onPaid({
      razorpayOrderId: r.razorpay_order_id,
      razorpayPaymentId: r.razorpay_payment_id,
      razorpaySignature: r.razorpay_signature,
    }),
    modal: { ondismiss: handlers.onDismiss },
  });

  instance.on('payment.failed', (e) => handlers.onFailed(e.error?.description ?? e.error?.reason ?? null));
  instance.open();
}
